import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FiTrash2 } from 'react-icons/fi';
import CrewCopyFromDateCalendar from './CrewCopyFromDateCalendar';
import { ConfirmDialog } from './common/ConfirmDialog';
import EquipmentTimeCardUpdateMultipleModal from './EquipmentTimeCardUpdateMultipleModal';
import { useFullscreenSheetModal } from '../hooks/useFullscreenSheetModal';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { DailyLogBackButton, DailyLogBreadcrumbs, DailyLogProjectFields } from './DailyLogFlowNav';

/**
 * Equipment Time Card modal — layout from equipment_time_card_mockup.html,
 * visual language matched to Crew Timesheet (ModalCrewTimesheet).
 * Opens after a successful "Save and Log Equipment" on the crew timesheet.
 */

const isValidPartialHoursInput = (value) =>
  value === '' || /^\d*\.?\d*$/.test(value);

const isBlankRunTime = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text === '.') return true;
  const hours = Number(text);
  return !Number.isFinite(hours);
};

const RUN_TIME_REQUIRED_TITLE = 'Cannot save — Run Time required';
const RUN_TIME_REQUIRED_SUBTITLE =
  'Enter Time on Site (Hrs) for every equipment row before saving, or delete the rows you are not logging.';

const makeRowId = () =>
  `eq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const formatWorkDateDisplay = (isoDate) => {
  const raw = String(isoDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '—';
  const [y, m, d] = raw.split('-');
  return `${m}-${d}-${y}`;
};

function friendlySaveError(message) {
  const text = String(message || '').trim();
  if (/duplicate key|unique constraint|insert into/i.test(text)) {
    return 'Could not save run time. Please try again.';
  }
  return text || 'Could not save equipment.';
}

function measurePopup(el, preferredWidth = 420) {
  const margin = 8;
  const rect = el.getBoundingClientRect();
  const width = Math.min(
    Math.max(rect.width, preferredWidth),
    Math.max(margin * 2, window.innerWidth - margin * 2)
  );
  const left = Math.min(
    Math.max(margin, rect.left),
    Math.max(margin, window.innerWidth - width - margin)
  );
  return {
    left,
    bottom: Math.max(margin, window.innerHeight - rect.top + 8),
    width,
  };
}

function CatalogMultiSelectPopup({
  title,
  totalCount,
  showingCount,
  searchActive,
  noun,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  loading,
  error,
  emptyMessage,
  noMatchMessage,
  options,
  onToggle,
  onClose,
  onUpdate,
  updateDisabled,
  position,
}) {
  if (!position) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 z-[10340]" onClick={onClose} />
      <div
        className="fixed z-[10350] rounded-lg bg-white border border-gray-200 shadow-xl"
        style={{
          left: position.left,
          bottom: position.bottom,
          width: position.width,
        }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {!loading && !error && (
              <p className="text-xs text-gray-500 mt-0.5">
                Total:{' '}
                <span className="font-semibold text-gray-700">{totalCount}</span>
                {searchActive ? ` · Showing ${showingCount}` : ` ${noun}`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="px-4 py-2 border-b border-gray-100">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm"
            autoFocus
          />
        </div>
        <div className="px-4 py-3 max-h-[min(280px,42dvh)] overflow-y-auto overscroll-contain">
          {loading && <p className="text-sm text-gray-500">Loading equipment…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && options.length === 0 && (
            <p className="text-sm text-gray-500">
              {totalCount === 0 ? emptyMessage : noMatchMessage}
            </p>
          )}
          {!loading &&
            !error &&
            options.map((item) => (
              <label
                key={item.id}
                className={`flex items-center gap-2 py-1.5 text-sm cursor-pointer ${
                  item.disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-800'
                }`}
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  disabled={item.disabled}
                  onChange={() => onToggle(item.id)}
                />
                <span className="truncate" title={item.label}>
                  {item.label}
                  {item.disabled ? ' (added)' : ''}
                </span>
              </label>
            ))}
        </div>
        <div className="px-4 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onUpdate}
            disabled={updateDisabled}
            className="h-9 w-full rounded-md bg-[#AD2226] text-sm font-medium text-white hover:bg-[#8F1B1E] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Update
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   projectCode?: string,
 *   projectName?: string,
 *   workDate?: string,
 *   onWorkDateChange?: (date: string) => void,
 *   activity?: string,
 *   scopeName?: string,
 *   scopeId?: string,
 *   onLogProduction?: () => void,
 *   onFlowNavigate?: (step: string) => void,
 * }} props
 */
function ModalEquipmentTimeCard({
  isOpen,
  onClose,
  projectCode = '',
  projectName = '—',
  workDate = '',
  onWorkDateChange,
  activity = '—',
  scopeName = '',
  scopeId = '',
  onLogProduction,
  onFlowNavigate,
}) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const addEquipmentBtnRef = useRef(null);
  const addGroupBtnRef = useRef(null);
  const copyEquipmentBtnRef = useRef(null);
  const loadedKeyRef = useRef('');
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const latestSnapshotRef = useRef(null);
  const savePumpRef = useRef(null);
  const committedFieldsRef = useRef(new Map());
  const postEntriesRef = useRef(async () => true);
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [isAddEquipmentOpen, setIsAddEquipmentOpen] = useState(false);
  const [addEquipmentPopupPos, setAddEquipmentPopupPos] = useState(null);
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState('');
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [addGroupPopupPos, setAddGroupPopupPos] = useState(null);
  const [crewLeaderSearchQuery, setCrewLeaderSearchQuery] = useState('');
  const [selectedCrewLeaderIds, setSelectedCrewLeaderIds] = useState([]);
  const [isCopyEquipmentOpen, setIsCopyEquipmentOpen] = useState(false);
  const [copyEquipmentPopupPos, setCopyEquipmentPopupPos] = useState(null);
  const [savedEquipmentDates, setSavedEquipmentDates] = useState([]);
  const [savedEquipmentDatesLoading, setSavedEquipmentDatesLoading] = useState(false);
  const [savedEquipmentDatesError, setSavedEquipmentDatesError] = useState(null);
  const [isCopyingEquipment, setIsCopyingEquipment] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState(null);
  const [loggingProduction, setLoggingProduction] = useState(false);
  const [showRunTimeErrors, setShowRunTimeErrors] = useState(false);
  const [isSelectDeleteMode, setIsSelectDeleteMode] = useState(false);
  const [isUpdateMultipleOpen, setIsUpdateMultipleOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, rows: [] });
  const [deletingRows, setDeletingRows] = useState(false);
  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    // Reset card contents only when the modal opens (same session fields stay live from parent).
    setMessage(null);
    setIsAddEquipmentOpen(false);
    setEquipmentSearchQuery('');
    setSelectedEquipmentIds([]);
    setIsAddGroupOpen(false);
    setCrewLeaderSearchQuery('');
    setSelectedCrewLeaderIds([]);
    setIsCopyEquipmentOpen(false);
    setShowRunTimeErrors(false);
    setIsSelectDeleteMode(false);
    setIsUpdateMultipleOpen(false);
    setSelectedRowIds([]);
    setDeleteConfirm({ isOpen: false, rows: [] });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isReady) return undefined;
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setCatalogError('Backend URL not configured.');
      setCatalog([]);
      return undefined;
    }
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    (async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/api/miter/production-performance/equipment-time-cards`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (res.data?.status === 'success' && Array.isArray(res.data?.data)) {
          setCatalog(res.data.data);
        } else {
          setCatalog([]);
        }
      } catch (err) {
        if (cancelled) return;
        setCatalog([]);
        setCatalogError(
          err.response?.data?.message || err.message || 'Failed to load equipment.'
        );
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isReady, getAuthConfig]);

  const entryContext = useMemo(() => {
    const project = String(projectCode || '').trim();
    const date = String(workDate || '').trim();
    const scope = String(scopeName || '').trim();
    if (
      !project ||
      project === '—' ||
      !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
      !scope ||
      scope === '—' ||
      scope === 'Loading…'
    ) {
      return null;
    }
    return {
      project,
      date,
      scope,
      scopeId: scopeId ? String(scopeId) : '',
    };
  }, [projectCode, workDate, scopeName, scopeId]);

  const entryKey = entryContext
    ? `${entryContext.project}|${entryContext.date}|${entryContext.scope}`
    : '';

  const sendSnapshot = async (snapshot) => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl || !snapshot) return false;
    try {
      const res = await axios.put(
        `${baseUrl}/api/miter/production-performance/${encodeURIComponent(snapshot.project)}/equipment-time-card/${snapshot.date}`,
        {
          scope: snapshot.scope,
          scopeId: snapshot.scopeId || null,
          items: (snapshot.rows || []).map((row) => ({
            equipmentMasterId: row.catalogId,
            equipmentId: row.equipmentId,
            unitNumber: row.unitNumber,
            description: row.description,
            runTimeHours: row.runTimeHours,
            dailyNotes: row.dailyNotes,
          })),
        },
        await getAuthConfig()
      );
      if (latestSnapshotRef.current && latestSnapshotRef.current !== snapshot) return true;
      if (res.data?.status !== 'success') {
        setEntriesError(friendlySaveError(res.data?.message || 'Failed to save equipment.'));
        return false;
      }
      const committed = new Map();
      (snapshot.rows || []).forEach((row) => {
        if (!row.equipmentId) return;
        committed.set(row.equipmentId, {
          runTimeHours: row.runTimeHours ?? '',
          dailyNotes: row.dailyNotes ?? '',
        });
      });
      committedFieldsRef.current = committed;
      setEntriesError(null);
      return true;
    } catch (err) {
      if (latestSnapshotRef.current && latestSnapshotRef.current !== snapshot) return true;
      setEntriesError(
        friendlySaveError(
          err.response?.data?.message || err.message || 'Failed to save equipment.'
        )
      );
      return false;
    }
  };

  const postEntries = (snapshot) => {
    if (!snapshot) return Promise.resolve(false);
    latestSnapshotRef.current = snapshot;
    if (!savePumpRef.current) {
      savePumpRef.current = (async () => {
        let ok = true;
        try {
          while (latestSnapshotRef.current) {
            const next = latestSnapshotRef.current;
            latestSnapshotRef.current = null;
            ok = await sendSnapshot(next);
          }
        } finally {
          savePumpRef.current = null;
        }
        if (latestSnapshotRef.current) {
          return postEntriesRef.current(latestSnapshotRef.current);
        }
        return ok;
      })();
    }
    return savePumpRef.current;
  };
  postEntriesRef.current = postEntries;

  const rowsForRosterSave = (nextRows) =>
    nextRows.map((row) => {
      const committed = committedFieldsRef.current.get(row.equipmentId);
      return {
        ...row,
        runTimeHours: committed ? committed.runTimeHours : '',
        dailyNotes: committed ? committed.dailyNotes : '',
      };
    });

  const scheduleSave = (nextRows, { immediate = false } = {}) => {
    if (!entryContext || loadedKeyRef.current !== entryKey) return;
    const snapshot = {
      project: entryContext.project,
      date: entryContext.date,
      scope: entryContext.scope,
      scopeId: entryContext.scopeId,
      rows: nextRows,
    };
    pendingSaveRef.current = snapshot;
    const send = () => {
      if (pendingSaveRef.current !== snapshot) return;
      pendingSaveRef.current = null;
      postEntriesRef.current(snapshot);
    };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (immediate) {
      send();
      return;
    }
    saveTimerRef.current = setTimeout(send, 400);
  };

  const flushSave = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const snapshot = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (snapshot) return postEntriesRef.current(snapshot);
    if (savePumpRef.current) return savePumpRef.current;
    return Promise.resolve(true);
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const snapshot = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (snapshot) postEntriesRef.current(snapshot);
    };
  }, []);

  useEffect(() => {
    if (isOpen) return undefined;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const snapshot = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (snapshot) postEntriesRef.current(snapshot);
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!entryContext || !isReady) {
      if (!entryContext) {
        setRows([]);
        loadedKeyRef.current = '';
      }
      return undefined;
    }
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setEntriesError('Backend URL not configured.');
      return undefined;
    }
    let cancelled = false;
    const key = entryKey;
    loadedKeyRef.current = '';
    committedFieldsRef.current = new Map();
    setRows([]);
    setSelectedRowIds([]);
    setIsSelectDeleteMode(false);
    setIsUpdateMultipleOpen(false);
    setEntriesLoading(true);
    setEntriesError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ scope: entryContext.scope });
        const res = await axios.get(
          `${baseUrl}/api/miter/production-performance/${encodeURIComponent(entryContext.project)}/equipment-time-card/${entryContext.date}?${params.toString()}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        const entries = Array.isArray(res.data?.data?.entries) ? res.data.data.entries : [];
        const committed = new Map();
        const nextRows = entries.map((entry) => {
          const equipmentId = entry.equipmentId || '';
          const runTimeHours = entry.runTimeHours ?? '';
          const dailyNotes = entry.dailyNotes || '';
          if (equipmentId) {
            committed.set(equipmentId, { runTimeHours, dailyNotes });
          }
          return {
            id: `saved-${entry.id}`,
            catalogId:
              entry.equipmentMasterId != null ? String(entry.equipmentMasterId) : '',
            equipmentId,
            unitNumber: entry.unitNumber || '',
            description: entry.description || '',
            runTimeHours,
            dailyNotes,
          };
        });
        committedFieldsRef.current = committed;
        setRows(nextRows);
        loadedKeyRef.current = key;
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setEntriesError(
          err.response?.data?.message || err.message || 'Failed to load saved equipment.'
        );
      } finally {
        if (!cancelled) setEntriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isReady, entryKey, entryContext, getAuthConfig]);

  useFullscreenSheetModal(isOpen, { skipLock: true });
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (isUpdateMultipleOpen) return;
      if (isSelectDeleteMode) {
        setIsSelectDeleteMode(false);
        setSelectedRowIds([]);
        return;
      }
      if (isCopyEquipmentOpen) {
        setIsCopyEquipmentOpen(false);
        return;
      }
      if (isAddGroupOpen) {
        setIsAddGroupOpen(false);
        return;
      }
      if (isAddEquipmentOpen) {
        setIsAddEquipmentOpen(false);
        return;
      }
      onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isOpen,
    onClose,
    isAddEquipmentOpen,
    isAddGroupOpen,
    isCopyEquipmentOpen,
    isSelectDeleteMode,
    isUpdateMultipleOpen,
  ]);

  const existingEquipmentKeys = useMemo(() => {
    const keys = new Set();
    rows.forEach((row) => {
      if (row.catalogId != null) keys.add(`id:${row.catalogId}`);
      if (row.equipmentId) keys.add(`eq:${row.equipmentId}`);
      if (row.unitNumber) keys.add(`unit:${row.unitNumber}`);
    });
    return keys;
  }, [rows]);

  const equipmentOptions = useMemo(
    () =>
      (catalog || [])
        .filter((item) => item && String(item.description || '').trim() && item.is_active !== false)
        .map((item) => ({
          id: String(item.id),
          equipmentId: String(item.equipment_id || '').trim(),
          unitNumber: String(item.unit_number || '').trim(),
          description: String(item.description || '').trim(),
          crewLeader: String(item.crew_leader || '').trim(),
        })),
    [catalog]
  );

  const filteredEquipmentOptions = useMemo(() => {
    const query = String(equipmentSearchQuery || '').trim().toLowerCase();
    if (!query) return equipmentOptions;
    return equipmentOptions.filter((item) => {
      const description = item.description.toLowerCase();
      const unitNumber = item.unitNumber.toLowerCase();
      const equipmentId = item.equipmentId.toLowerCase();
      return (
        description.includes(query) ||
        unitNumber.includes(query) ||
        equipmentId.includes(query)
      );
    });
  }, [equipmentOptions, equipmentSearchQuery]);

  const selectedEquipmentToAdd = equipmentOptions.filter((item) =>
    selectedEquipmentIds.includes(item.id)
  );

  const crewLeaderOptions = useMemo(() => {
    const byName = new Map();
    equipmentOptions.forEach((item) => {
      if (!item.crewLeader) return;
      const id = item.crewLeader.toLowerCase();
      if (!byName.has(id)) {
        byName.set(id, { id, name: item.crewLeader, items: [] });
      }
      byName.get(id).items.push(item);
    });
    return Array.from(byName.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [equipmentOptions]);

  const filteredCrewLeaderOptions = useMemo(() => {
    const query = String(crewLeaderSearchQuery || '').trim().toLowerCase();
    if (!query) return crewLeaderOptions;
    return crewLeaderOptions.filter((leader) => leader.name.toLowerCase().includes(query));
  }, [crewLeaderOptions, crewLeaderSearchQuery]);

  const selectedCrewLeaders = crewLeaderOptions.filter((leader) =>
    selectedCrewLeaderIds.includes(leader.id)
  );

  useEffect(() => {
    if (!isAddEquipmentOpen) {
      setAddEquipmentPopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = addEquipmentBtnRef.current;
      if (!el) return;
      setAddEquipmentPopupPos(measurePopup(el));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isAddEquipmentOpen]);

  useEffect(() => {
    if (!isAddGroupOpen) {
      setAddGroupPopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = addGroupBtnRef.current;
      if (!el) return;
      setAddGroupPopupPos(measurePopup(el));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isAddGroupOpen]);

  useEffect(() => {
    if (!isCopyEquipmentOpen) {
      setCopyEquipmentPopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = copyEquipmentBtnRef.current;
      if (!el) return;
      setCopyEquipmentPopupPos(measurePopup(el, 300));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isCopyEquipmentOpen]);

  useEffect(() => {
    if (!isCopyEquipmentOpen || !isReady) return undefined;
    setSavedEquipmentDatesError(null);
    if (!entryContext) {
      setSavedEquipmentDates([]);
      setSavedEquipmentDatesError('Project, work date, and activity are required.');
      return undefined;
    }
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setSavedEquipmentDates([]);
      setSavedEquipmentDatesError('Backend URL not configured.');
      return undefined;
    }
    let cancelled = false;
    setSavedEquipmentDatesLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ scope: entryContext.scope });
        if (entryContext.date) params.set('excludeWorkDate', entryContext.date);
        const res = await axios.get(
          `${baseUrl}/api/miter/production-performance/${encodeURIComponent(
            entryContext.project
          )}/equipment-time-card-dates?${params.toString()}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (res.data?.status === 'success' && Array.isArray(res.data?.data?.dates)) {
          setSavedEquipmentDates(res.data.data.dates);
        } else {
          setSavedEquipmentDates([]);
        }
      } catch (err) {
        if (cancelled) return;
        setSavedEquipmentDates([]);
        setSavedEquipmentDatesError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load saved equipment dates.'
        );
      } finally {
        if (!cancelled) setSavedEquipmentDatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCopyEquipmentOpen, isReady, entryContext, getAuthConfig]);

  const isEquipmentOnCard = (item) =>
    existingEquipmentKeys.has(`id:${item.id}`) ||
    (item.equipmentId && existingEquipmentKeys.has(`eq:${item.equipmentId}`)) ||
    (item.unitNumber && existingEquipmentKeys.has(`unit:${item.unitNumber}`));

  const addEquipmentItems = (items, emptySelectionMessage) => {
    const unique = [];
    const seen = new Set();
    items.forEach((item) => {
      const key = item.equipmentId || item.id;
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(item);
    });
    const fresh = unique.filter((item) => !isEquipmentOnCard(item));
    if (fresh.length === 0) {
      setMessage({
        type: 'error',
        text:
          unique.length > 0
            ? 'Selected equipment is already on this time card.'
            : emptySelectionMessage,
      });
      return false;
    }
    const nextRows = [
      ...rows,
      ...fresh.map((item) => ({
        id: makeRowId(),
        catalogId: item.id,
        equipmentId: item.equipmentId,
        unitNumber: item.unitNumber || item.equipmentId,
        description: item.description,
        runTimeHours: '',
        dailyNotes: '',
      })),
    ];
    setRows(nextRows);
    scheduleSave(rowsForRosterSave(nextRows), { immediate: true });
    const skipped = unique.length - fresh.length;
    setMessage(
      skipped > 0
        ? {
            type: 'success',
            text: `Added ${fresh.length} equipment item${fresh.length === 1 ? '' : 's'}. ${skipped} already on this time card.`,
          }
        : null
    );
    return true;
  };

  const handleToggleAddEquipment = () => {
    setMessage(null);
    setIsAddGroupOpen(false);
    setIsCopyEquipmentOpen(false);
    setIsAddEquipmentOpen((open) => !open);
    setEquipmentSearchQuery('');
    setSelectedEquipmentIds([]);
  };

  const handleToggleAddGroup = () => {
    setMessage(null);
    setIsAddEquipmentOpen(false);
    setIsCopyEquipmentOpen(false);
    setIsAddGroupOpen((open) => !open);
    setCrewLeaderSearchQuery('');
    setSelectedCrewLeaderIds([]);
  };

  const handleToggleCopyEquipment = () => {
    setMessage(null);
    setIsAddEquipmentOpen(false);
    setIsAddGroupOpen(false);
    setIsCopyEquipmentOpen((open) => !open);
  };

  const handleCopyEquipmentFromDate = async (fromDate) => {
    const sourceDate = String(fromDate || '').trim();
    const dateInfo = savedEquipmentDates.find((d) => String(d.workDate) === sourceDate);
    if (!sourceDate || !dateInfo || !entryContext || sourceDate === entryContext.date) return;

    setIsCopyingEquipment(true);
    setMessage(null);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
      const res = await axios.post(
        `${baseUrl}/api/miter/production-performance/${encodeURIComponent(
          entryContext.project
        )}/equipment-time-card/copy-from-date`,
        {
          fromWorkDate: sourceDate,
          workDate: entryContext.date,
          scope: entryContext.scope,
          scopeId: entryContext.scopeId || null,
        },
        await getAuthConfig()
      );
      const returned = Array.isArray(res.data?.data?.entries) ? res.data.data.entries : [];
      const existingKeys = new Set(rows.map((row) => String(row.equipmentId || '').toLowerCase()));
      const added = returned
        .filter((entry) => !existingKeys.has(String(entry.equipmentId || '').toLowerCase()))
        .map((entry) => ({
          id: makeRowId(),
          catalogId:
            entry.equipmentMasterId != null ? String(entry.equipmentMasterId) : '',
          equipmentId: entry.equipmentId,
          unitNumber: entry.unitNumber || '',
          description: entry.description || '',
          runTimeHours: '',
          dailyNotes: '',
        }));
      if (added.length > 0) {
        const committed = new Map(committedFieldsRef.current);
        added.forEach((row) => {
          if (!row.equipmentId || committed.has(row.equipmentId)) return;
          committed.set(row.equipmentId, { runTimeHours: '', dailyNotes: '' });
        });
        committedFieldsRef.current = committed;
        const nextRows = [...rows, ...added];
        setRows(nextRows);
        scheduleSave(rowsForRosterSave(nextRows), { immediate: true });
      }
      const copiedCount = Number(res.data?.data?.copiedCount) || added.length;
      const fromLabel = formatWorkDateDisplay(res.data?.data?.fromWorkDate || sourceDate);
      setIsCopyEquipmentOpen(false);
      setMessage({
        type: 'success',
        text:
          copiedCount === 0
            ? `Those units are already on this time card.`
            : copiedCount === 1
              ? `Copied 1 equipment item from ${fromLabel}.`
              : `Copied ${copiedCount} equipment items from ${fromLabel}.`,
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.message ||
          err.message ||
          'Failed to copy equipment from the selected date.',
      });
    } finally {
      setIsCopyingEquipment(false);
    }
  };

  const handleAddSelectedEquipment = () => {
    const added = addEquipmentItems(
      selectedEquipmentToAdd,
      'Select at least one equipment description.'
    );
    if (!added) return;
    setIsAddEquipmentOpen(false);
    setEquipmentSearchQuery('');
    setSelectedEquipmentIds([]);
  };

  const handleAddSelectedCrewLeaders = () => {
    const items = selectedCrewLeaders.flatMap((leader) => leader.items);
    const added = addEquipmentItems(
      items,
      'Select at least one crew leader.'
    );
    if (!added) return;
    setIsAddGroupOpen(false);
    setCrewLeaderSearchQuery('');
    setSelectedCrewLeaderIds([]);
  };

  const handleRemoveRows = async (targets) => {
    const idSet = new Set(targets.map((row) => String(row.id)));
    const nextRows = rows.filter((row) => !idSet.has(String(row.id)));
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    pendingSaveRef.current = null;
    setDeletingRows(true);
    setRows(nextRows);
    let saved = true;
    if (entryContext && loadedKeyRef.current === entryKey) {
      saved = await postEntriesRef.current({
        project: entryContext.project,
        date: entryContext.date,
        scope: entryContext.scope,
        scopeId: entryContext.scopeId,
        rows: nextRows,
      });
    }
    setDeletingRows(false);
    if (!saved) {
      setRows(rows);
      setMessage({
        type: 'error',
        text: 'Could not delete the selected equipment. Please try again.',
      });
      return;
    }
    setDeleteConfirm({ isOpen: false, rows: [] });
    if (targets.length > 1 || nextRows.length === 0) {
      setIsSelectDeleteMode(false);
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds((prev) => prev.filter((id) => !idSet.has(String(id))));
    }
    setMessage({
      type: 'success',
      text:
        targets.length === 1
          ? `${targets[0].unitNumber || 'Equipment'} removed from this time card.`
          : `${targets.length} equipment rows removed from this time card.`,
    });
  };

  const handleOpenDeleteConfirm = (row) => {
    if (deletingRows) return;
    setDeleteConfirm({ isOpen: true, rows: [row] });
  };

  const handleEnterSelectDeleteMode = () => {
    if (rows.length === 0 || entriesLoading || deletingRows) return;
    setIsAddEquipmentOpen(false);
    setIsAddGroupOpen(false);
    setIsCopyEquipmentOpen(false);
    setSelectedRowIds([]);
    setIsSelectDeleteMode(true);
  };

  const handleExitSelectDeleteMode = () => {
    setIsSelectDeleteMode(false);
    setSelectedRowIds([]);
  };

  const handleToggleRowSelected = (rowId, checked) => {
    const id = String(rowId);
    setSelectedRowIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((value) => value !== id);
    });
  };

  const handleToggleAllRowsSelected = (checked) => {
    setSelectedRowIds(checked ? rows.map((row) => String(row.id)) : []);
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (deletingRows || selectedRowIds.length === 0) return;
    const selected = new Set(selectedRowIds);
    const toDelete = rows.filter((row) => selected.has(String(row.id)));
    if (toDelete.length === 0) return;
    setDeleteConfirm({ isOpen: true, rows: toDelete });
  };

  const handleCloseDeleteConfirm = () => {
    if (deletingRows) return;
    setDeleteConfirm({ isOpen: false, rows: [] });
  };

  const handleConfirmDeleteRows = () => {
    const targets = deleteConfirm.rows || [];
    if (targets.length === 0 || deletingRows) return;
    handleRemoveRows(targets);
  };

  const handleRunTimeChange = (rowId, value) => {
    if (!isValidPartialHoursInput(value)) return;
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, runTimeHours: value } : r))
    );
  };

  const handleUpdateMultipleRunTime = ({ rowIds, runTimeHours }) => {
    const selected = new Set((rowIds || []).map((id) => String(id)));
    if (selected.size === 0) return;
    setRows((prev) =>
      prev.map((row) =>
        selected.has(String(row.id)) ? { ...row, runTimeHours } : row
      )
    );
    setMessage({
      type: 'success',
      text:
        selected.size === 1
          ? 'Run time updated for 1 equipment row.'
          : `Run time updated for ${selected.size} equipment rows.`,
    });
  };

  const handleNotesChange = (rowId, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, dailyNotes: value } : r))
    );
  };

  useEffect(() => {
    if (!showRunTimeErrors) return;
    if (rows.some((row) => isBlankRunTime(row.runTimeHours))) return;
    setShowRunTimeErrors(false);
  }, [rows, showRunTimeErrors]);

  const handleSaveAndLogProduction = async () => {
    if (rows.some((row) => isBlankRunTime(row.runTimeHours))) {
      setShowRunTimeErrors(true);
      return;
    }
    setShowRunTimeErrors(false);
    setLoggingProduction(true);
    let saved = true;
    if (entryContext && loadedKeyRef.current === entryKey) {
      saved = await postEntriesRef.current({
        project: entryContext.project,
        date: entryContext.date,
        scope: entryContext.scope,
        scopeId: entryContext.scopeId,
        rows,
      });
    } else {
      saved = await flushSave();
    }
    setLoggingProduction(false);
    if (!saved) return;
    setMessage({
      type: 'success',
      text: 'Equipment time card saved.',
    });
    if (typeof onLogProduction === 'function') {
      onLogProduction();
      return;
    }
    onClose?.();
  };

  const selectedRowIdSet = useMemo(
    () => new Set(selectedRowIds),
    [selectedRowIds]
  );
  const selectedCount = useMemo(
    () =>
      rows.reduce(
        (count, row) =>
          count + (selectedRowIdSet.has(String(row.id)) ? 1 : 0),
        0
      ),
    [rows, selectedRowIdSet]
  );
  const allRowsSelected = rows.length > 0 && selectedCount === rows.length;

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate =
      selectedCount > 0 && !allRowsSelected;
  }, [selectedCount, allRowsSelected]);

  if (!isOpen) return null;

  const deleteTargets = deleteConfirm.rows || [];
  const isBulkDelete = deleteTargets.length > 1;
  const deleteTargetLabel = isBulkDelete
    ? `${deleteTargets.length} equipment rows`
    : deleteTargets[0]?.unitNumber
      ? `unit ${deleteTargets[0].unitNumber}`
      : 'this equipment';
  const tableColSpan = isSelectDeleteMode ? 6 : 5;

  return (
    <>
      {createPortal(
    <div className="fixed inset-0 z-[10240] items-center justify-center bg-black/50">
      <div className="nettles-flow-sheet-panel absolute inset-2 lg:inset-6 bg-white rounded-xl shadow-2xl flex flex-col overflow-x-clip overflow-y-hidden max-lg:top-[max(0.5rem,env(safe-area-inset-top))] max-lg:bottom-[max(0.5rem,env(safe-area-inset-bottom))] max-lg:left-[max(0.5rem,env(safe-area-inset-left))] max-lg:right-[max(0.5rem,env(safe-area-inset-right))]">
        <div className="relative z-10 flex shrink-0 items-center justify-between gap-3 sm:gap-4 bg-white py-3.5 sm:py-[18px] px-4 sm:px-6 rounded-t-xl border-b border-gray-200 shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
          {typeof onFlowNavigate === 'function' ? (
            <DailyLogBreadcrumbs
              currentStep="equipment"
              onStepChange={onFlowNavigate}
              disabled={loggingProduction}
            />
          ) : (
            <h2 className="font-poppins text-[18px] sm:text-[20px] font-bold text-[#2C2C2C]">
              Equipment Time Card
            </h2>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-md hover:bg-gray-100 touch-manipulation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
                fill="#424143"
              />
            </svg>
          </button>
        </div>

        {showRunTimeErrors && (
          <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-white px-6 py-3">
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-500"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {RUN_TIME_REQUIRED_TITLE}
                </p>
                <p className="mt-0.5 text-sm text-amber-800/90">
                  {RUN_TIME_REQUIRED_SUBTITLE}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowRunTimeErrors(false)}
                className="shrink-0 text-sm text-amber-700 underline hover:text-amber-900"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="nettles-flow-sheet-scroll flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden max-lg:landscape:overflow-y-auto max-lg:landscape:[scrollbar-gutter:stable] max-lg:landscape:[-webkit-overflow-scrolling:touch]">
        <div className="nettles-flow-sheet-body flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-clip max-lg:landscape:flex-none max-lg:landscape:overflow-y-visible">
          <DailyLogProjectFields
            projectNo={projectCode}
            projectName={projectName}
            workDate={workDate}
            onWorkDateChange={onWorkDateChange}
            activity={activity}
          />

          <div className="px-6 py-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              <span className="font-medium text-[#1E5A8A]">
                {rows.length} equipment
              </span>
              <span className="mx-1.5 text-gray-300" aria-hidden>
                ·
              </span>
              Log equipment run time for this work date. Add equipment, then enter
              hours and daily notes.
            </p>
            {isSelectDeleteMode ? (
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  {selectedCount > 0
                    ? `${selectedCount} selected`
                    : 'Select equipment to delete'}
                </span>
                <button
                  type="button"
                  onClick={handleExitSelectDeleteMode}
                  disabled={deletingRows}
                  className="h-9 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleOpenBulkDeleteConfirm}
                  disabled={deletingRows || selectedCount === 0}
                  className="h-9 px-4 rounded-md border border-red-200 bg-white text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete selected
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4 shrink-0">
                <button
                  type="button"
                  onClick={handleEnterSelectDeleteMode}
                  disabled={entriesLoading || rows.length === 0}
                  className="h-9 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Multiple
                </button>
                <button
                  type="button"
                  onClick={() => setIsUpdateMultipleOpen(true)}
                  disabled={entriesLoading || rows.length === 0}
                  className="flex items-center gap-2 text-[15px] sm:text-[16px] font-normal text-[#AD2226] underline font-poppins leading-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-6 h-6"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6Z"
                      fill="#AD2226"
                    />
                    <path
                      d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z"
                      fill="#AD2226"
                    />
                    <path
                      d="M12 20C10.8954 20 10 19.1046 10 18C10 16.8954 10.8954 16 12 16C13.1046 16 14 16.8954 14 18C14 19.1046 13.1046 20 12 20Z"
                      fill="#AD2226"
                    />
                  </svg>
                  <span>Update Multiple</span>
                </button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="bg-[#EAF4FB]">
                  {isSelectDeleteMode && (
                    <th className="w-10 px-2 py-3 text-center bg-[#EAF4FB] border-b border-r border-gray-200">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allRowsSelected}
                        onChange={(e) =>
                          handleToggleAllRowsSelected(e.target.checked)
                        }
                        disabled={deletingRows || rows.length === 0}
                        aria-label="Select all equipment"
                        className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226] disabled:opacity-50"
                      />
                    </th>
                  )}
                  <th className="w-12 px-2 py-3 bg-[#EAF4FB] border-b border-r border-gray-200" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 bg-[#EAF4FB] border-b border-r border-gray-200 min-w-[120px]">
                    Unit Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 bg-[#EAF4FB] border-b border-r border-gray-200 min-w-[180px]">
                    Description
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 bg-[#EAF4FB] border-b border-r border-gray-200 min-w-[120px]">
                    Time on Site (Hrs)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 bg-[#EAF4FB] border-b border-gray-200 min-w-[220px]">
                    Daily Notes
                  </th>
                </tr>
              </thead>
              <tbody>
                {entriesLoading ? (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="px-4 py-10 text-center text-sm text-gray-500 border-t border-gray-100"
                    >
                      Loading saved equipment…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="px-4 py-10 text-center text-sm text-gray-500 border-t border-gray-100"
                    >
                      No equipment on this time card yet. Use Add Equipment below.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const isSelected = selectedRowIdSet.has(String(row.id));
                    const bandBg = isSelected
                      ? 'bg-red-50'
                      : index % 2 === 0
                        ? 'bg-white'
                        : 'bg-gray-50/60';
                    return (
                      <tr
                        key={row.id}
                        className={`border-t border-gray-100 ${bandBg}`}
                      >
                        {isSelectDeleteMode && (
                          <td className="px-2 py-2 text-center border-r border-gray-100">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) =>
                                handleToggleRowSelected(row.id, e.target.checked)
                              }
                              disabled={deletingRows}
                              aria-label={`Select unit ${row.unitNumber || row.description || 'equipment'}`}
                              className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226] disabled:opacity-50"
                            />
                          </td>
                        )}
                        <td className="px-2 py-2 text-center border-r border-gray-100">
                          <button
                            type="button"
                            onClick={() => handleOpenDeleteConfirm(row)}
                            disabled={deletingRows}
                            className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Remove equipment"
                            aria-label={`Remove ${row.unitNumber}`}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="px-4 py-2 border-r border-gray-100 text-sm text-gray-900 font-medium">
                          {row.unitNumber}
                        </td>
                        <td className="px-4 py-2 border-r border-gray-100 text-sm text-gray-800">
                          {row.description}
                        </td>
                        <td className="px-2 py-2 border-r border-gray-100">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={row.runTimeHours}
                            onChange={(e) =>
                              handleRunTimeChange(row.id, e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                            aria-invalid={
                              showRunTimeErrors && isBlankRunTime(row.runTimeHours)
                                ? true
                                : undefined
                            }
                            className={`h-9 w-full rounded-md border bg-white px-2 text-center text-sm text-[#2C2C2C] focus:outline-none focus:ring-2 ${
                              showRunTimeErrors && isBlankRunTime(row.runTimeHours)
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={row.dailyNotes}
                            onChange={(e) =>
                              handleNotesChange(row.id, e.target.value)
                            }
                            className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Notes…"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <button
              ref={addEquipmentBtnRef}
              type="button"
              onClick={handleToggleAddEquipment}
              disabled={entriesLoading}
              className={`h-9 px-4 rounded-md border text-sm font-medium disabled:opacity-60 ${
                isAddEquipmentOpen
                  ? 'border-gray-400 bg-gray-100 text-gray-900'
                  : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
              }`}
            >
              Add Equipment
            </button>
            <button
              ref={addGroupBtnRef}
              type="button"
              onClick={handleToggleAddGroup}
              disabled={entriesLoading}
              className={`h-9 px-4 rounded-md border text-sm font-medium disabled:opacity-60 ${
                isAddGroupOpen
                  ? 'border-gray-400 bg-gray-100 text-gray-900'
                  : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
              }`}
            >
              Add Equipment Group
            </button>
            <button
              ref={copyEquipmentBtnRef}
              type="button"
              onClick={handleToggleCopyEquipment}
              disabled={entriesLoading || isCopyingEquipment}
              className={`h-9 px-4 rounded-md border text-sm font-medium disabled:opacity-60 ${
                isCopyEquipmentOpen
                  ? 'border-gray-400 bg-gray-100 text-gray-900'
                  : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
              }`}
            >
              {isCopyingEquipment ? 'Copying…' : 'Copy Equipment from Another Date'}
            </button>

            {isCopyEquipmentOpen &&
              copyEquipmentPopupPos &&
              createPortal(
                <>
                  <div
                    className="fixed inset-0 z-[10340]"
                    onClick={() => {
                      if (!isCopyingEquipment) setIsCopyEquipmentOpen(false);
                    }}
                  />
                  <div
                    className="fixed z-[10350] rounded-lg bg-white border border-gray-200 shadow-xl p-3"
                    style={{
                      left: copyEquipmentPopupPos.left,
                      bottom: copyEquipmentPopupPos.bottom,
                      width: 300,
                    }}
                  >
                    {savedEquipmentDatesLoading && (
                      <p className="text-sm text-gray-500 px-1 py-2">Loading calendar…</p>
                    )}
                    {savedEquipmentDatesError && (
                      <p className="text-sm text-red-600 px-1 py-2">{savedEquipmentDatesError}</p>
                    )}
                    {!savedEquipmentDatesLoading &&
                      !savedEquipmentDatesError &&
                      savedEquipmentDates.length === 0 && (
                        <p className="text-sm text-gray-500 px-1 py-2">
                          No other saved equipment lists found for this activity.
                        </p>
                      )}
                    {!savedEquipmentDatesLoading &&
                      !savedEquipmentDatesError &&
                      savedEquipmentDates.length > 0 && (
                        <CrewCopyFromDateCalendar
                          availableDates={savedEquipmentDates}
                          excludeDate={workDate}
                          disabled={isCopyingEquipment}
                          countNoun="equipment item"
                          legend="Saved equipment — click to copy"
                          onSelect={(iso) => {
                            handleCopyEquipmentFromDate(iso);
                          }}
                        />
                      )}
                  </div>
                </>,
                document.body
              )}

            {isAddEquipmentOpen && (
              <CatalogMultiSelectPopup
                title="Add Equipment"
                totalCount={equipmentOptions.length}
                showingCount={filteredEquipmentOptions.length}
                searchActive={Boolean(equipmentSearchQuery.trim())}
                noun="equipment"
                searchQuery={equipmentSearchQuery}
                onSearchChange={setEquipmentSearchQuery}
                searchPlaceholder="Search description…"
                loading={catalogLoading}
                error={catalogError}
                emptyMessage="No equipment uploaded yet."
                noMatchMessage="No equipment matches that search."
                position={addEquipmentPopupPos}
                onClose={() => setIsAddEquipmentOpen(false)}
                onUpdate={handleAddSelectedEquipment}
                updateDisabled={selectedEquipmentToAdd.length === 0}
                onToggle={(id) =>
                  setSelectedEquipmentIds((prev) =>
                    prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
                  )
                }
                options={filteredEquipmentOptions.map((item) => ({
                  id: item.id,
                  label: item.description,
                  checked: selectedEquipmentIds.includes(item.id),
                  disabled: isEquipmentOnCard(item),
                }))}
              />
            )}

            {isAddGroupOpen && (
              <CatalogMultiSelectPopup
                title="Add Equipment Group"
                totalCount={crewLeaderOptions.length}
                showingCount={filteredCrewLeaderOptions.length}
                searchActive={Boolean(crewLeaderSearchQuery.trim())}
                noun="crew leaders"
                searchQuery={crewLeaderSearchQuery}
                onSearchChange={setCrewLeaderSearchQuery}
                searchPlaceholder="Search crew leader…"
                loading={catalogLoading}
                error={catalogError}
                emptyMessage="No crew leaders uploaded yet."
                noMatchMessage="No crew leader matches that search."
                position={addGroupPopupPos}
                onClose={() => setIsAddGroupOpen(false)}
                onUpdate={handleAddSelectedCrewLeaders}
                updateDisabled={selectedCrewLeaders.length === 0}
                onToggle={(id) =>
                  setSelectedCrewLeaderIds((prev) =>
                    prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
                  )
                }
                options={filteredCrewLeaderOptions.map((leader) => ({
                  id: leader.id,
                  label: leader.name,
                  checked: selectedCrewLeaderIds.includes(leader.id),
                  disabled: leader.items.every((item) => isEquipmentOnCard(item)),
                }))}
              />
            )}
          </div>

          {entriesError && (
            <p className="mt-3 text-sm text-red-600">{entriesError}</p>
          )}

          {message && (
            <p
              className={`mt-3 text-sm ${
                message.type === 'error' ? 'text-red-600' : 'text-green-700'
              }`}
            >
              {message.text}
            </p>
          )}
        </div>
        </div>

        <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col items-center gap-2">
            <div className="flex w-full flex-nowrap sm:flex-wrap items-center justify-center gap-3">
              {typeof onFlowNavigate === 'function' && (
                <DailyLogBackButton
                  currentStep="equipment"
                  onStepChange={onFlowNavigate}
                  disabled={loggingProduction || entriesLoading}
                />
              )}
              <button
                type="button"
                onClick={handleSaveAndLogProduction}
                disabled={loggingProduction || entriesLoading}
                className="h-12 sm:h-11 min-w-0 flex-1 sm:flex-none sm:w-auto sm:max-w-none sm:px-16 rounded-md bg-[#AD2226] text-sm font-semibold text-white hover:bg-[#8F1B1E] disabled:opacity-60 touch-manipulation"
              >
                {loggingProduction ? 'Saving…' : 'Save and Log Production'}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  )}
      <EquipmentTimeCardUpdateMultipleModal
        isOpen={isUpdateMultipleOpen}
        onClose={() => setIsUpdateMultipleOpen(false)}
        rows={rows}
        onUpdateRunTime={handleUpdateMultipleRunTime}
      />
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Equipment"
        message={`Are you sure you want to delete ${deleteTargetLabel} from this equipment time card?`}
        confirmLabel={isBulkDelete ? 'Delete selected' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteRows}
        onClose={handleCloseDeleteConfirm}
        isLoading={deletingRows}
        isDestructive
        overlayClassName="z-[10380]"
      />
    </>
  );
}

export default ModalEquipmentTimeCard;
